/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { apiClient } from '../../actions/api';
import { isUserManager } from '../../selectors/user';
import EditButton from '../button/EditButton';
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from 'reactstrap';
import { loadUniversalTrackerModal } from '../../actions/universalTrackerModal';
import { reloadInitiative } from '../../actions/initiative';
import Loader from '../loader'
import { getBubbles } from '../../selectors/universal-tracker-values';
import TargetActualModal from './TargetActualModal';
import { SimpleTooltip } from '../simple-tooltip';
import { Bubble, Bubbles } from './Bubbles';
import LoadingPlaceholder from '../LoaderContainer/LoadingPlaceholder';
import { SUMMARY_DASHBOARD_CIRCLES } from '../../constants/usage-slots';
import { DashboardSection, DashboardSectionButton } from '../dashboard';
import { RootState } from '../../reducers';
import { reloadUniversalTrackerValues } from '../../actions/universalTracker';
import { generateUrl } from '../../routes/util';
import { ROUTES } from '../../constants/routes';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import './styles.scss';
import { loadScorecardsByInitiativeId, loadScorecardsBySurveyId } from '../../actions/scorecard';

enum TargetFilter {
  Materiality = 'materiality',
  Custom = 'custom',
}

const menuItems = [
  { code: TargetFilter.Materiality, name: 'Materiality by Industry' },
  { code: TargetFilter.Custom, name: 'Customized Materiality' },
];

interface Props extends PropsFromRedux {
  surveyId?: string;
  readOnly: boolean;
  universalTrackers?: unknown[];
  openTargetActualModal: boolean;
  toggleTargetActualModal: () => void;
  activeTab: string,
  setActiveTab: (tab: string) => void
}

interface State {
  saving: boolean;
  showAddUtrModal: boolean;
  showRemoveUtrModal: boolean;
  showRemoveUtrModalId: string;
  apiMessage: string;
  dropdownOpen: boolean;
  targetFilter?: TargetFilter;
}

class TargetActual extends Component<Props & RouteComponentProps> {

  state: State = {
    saving: false,
    showAddUtrModal: false,
    showRemoveUtrModal: false,
    showRemoveUtrModalId: '',
    apiMessage: '',
    dropdownOpen: false,
    targetFilter: undefined
  };

  public componentDidMount() {
    this.updateTargetFilter();
  }

  componentDidUpdate = () => {
    this.updateTargetFilter();
  }

  private updateTargetFilter() {
    // And not manually selected
    if (this.state.targetFilter === undefined) {

      const { initiative } = this.props;
      if (initiative) {
        // It's loaded
        const view = initiative.displaySettings?.targetActual?.view;
        this.handleTargetFilter(view === TargetFilter.Custom ? TargetFilter.Custom : TargetFilter.Materiality);
      }
    }
  }

  handleTargetFilter = (filter: TargetFilter) => {
    this.setState({ targetFilter: filter });
  }

  handleAddUTr = async (utrIds: string[]) => {
    const { initiativeId } = this.props;
    this.setState({ saving: true });

    if (!initiativeId) {
      return;
    }
    return apiClient.addUniversalTrackerToUsage({
      initiativeId: initiativeId,
      utrIds: utrIds,
      usage: SUMMARY_DASHBOARD_CIRCLES
    })
      .then(() => {
        this.props.toggleTargetActualModal()
        this.setState({ saving: false, showAddUtrModal: false, apiMessage: ''});
        this.props.reloadInitiative();
      })
      .catch(this.handleAddError);
  }

  handleRemoveUTr = (utrId: string) => {
    const { initiativeId } = this.props;
    this.setState({ saving: true });

    if (!initiativeId) {
      return;
    }
    return apiClient.removeUniversalTrackerToUsage({
      initiativeId: initiativeId,
      utrIds: [utrId],
      usage: SUMMARY_DASHBOARD_CIRCLES
    })
      .then(() => {
        this.setState({ saving: false, showRemoveUtrModal: false, apiMessage: '' });
        this.props.reloadInitiative();
      })
      .catch(this.handleRemoveError);
  }

  handleCancel = () => {
    this.props.toggleTargetActualModal()
    this.setState({
      showRemoveUtrModal: false,
      saving: false,
      apiMessage: ''
    });
  }

  handleClearMessage = () => {
    this.setState({ apiMessage: '' });
  }

  handleRemoveError = (e: Error) => {
    this.setState({ showRemoveUtrModal: true, apiMessage: e.message, saving: false });
  }

  handleError = (e: Error) => {
    this.setState({ apiMessage: e.message, saving: false });
  }

  handleAddError = (e: Error) => {
    this.setState({ showAddUtrModal: true, apiMessage: e.message, saving: false });
  }

  toggleAddUTr = () => {
    this.setState({ showAddUtrModal: !this.state.showAddUtrModal, apiMessage: '' });
  }

  showRemoveUtrModalConfirmation = (showRemoveUtrModalId: string) => {
    this.setState({ showRemoveUtrModal: true, showRemoveUtrModalId: showRemoveUtrModalId });
  }

  getBubbles = (): Bubble[] => {
    const { bubbles } = this.props;

    if (this.state.targetFilter === 'custom') {
      return bubbles.custom;
    }

    return bubbles.materiality.length > 0 ? bubbles.materiality : bubbles.default;
  };

  getBubbleData = () => {
    const bubbles = this.getBubbles();
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    bubbles.sort((a, b) => collator.compare(a.name, b.name));
    return bubbles;
  };

  render() {
    const { universalTrackerValuesState, isManager } = this.props;

    if (!universalTrackerValuesState.loaded) {
      return <DashboardSection
        title='Targets and Latest Values'
        className='flickity-wrapper target-actual-container-wrapper'
        padding={2}
      >
        <LoadingPlaceholder height={200} className='d-block background-ThemeTextWhite' />
      </DashboardSection>
    }

    const bubbles = this.getBubbleData();
    if (bubbles.length === 0 && !isManager) {
      return <></>;
    }

    return (
      <DashboardSection
        title='Targets and Latest Values'
        className='flickity-wrapper target-actual-container-wrapper'
        padding={2}
        buttons={this.getControlComponents()}
      >
        {this.renderEmptyData(bubbles)}
        <Bubbles
          removeUTrConfirmation={this.showRemoveUtrModalConfirmation}
          loadUTrModal={this.props.loadUTrModal}
          isManager={isManager}
          bubbles={bubbles} />
        {this.renderRemoveUTrModal()}
        {this.renderTargetActualModal()}
      </DashboardSection>
    )
  }

  renderEmptyData(bubbles: Bubble[]) {
    if (bubbles.length > 0) {
      return <></>;
    }

    if (this.state.targetFilter === 'custom' && this.props.isManager) {
      return <div className='p-4 text-center'>
        Please add targets using the <i className='fal fa-cog' /> button above.
      </div>;
    }

    return <></>;
  }

  async handleCreateCustomReport() {
    const { initiativeId } = this.props;

    if (!initiativeId) {
      return;
    }

    return apiClient.createCustomReportFromBubbles(initiativeId)
      .then((report) => {
        this.setState({ saving: false, showRemoveUtrModal: false, apiMessage: '' });
        this.props.reloadInitiative();
        const url = generateUrl(ROUTES.DOWNLOADS_CUSTOM, { initiativeId, reportId: report._id });
        this.props.history.push(url);
      })
      .catch(this.handleError);
  }

  getControlComponents() {
    const { targetFilter } = this.state;
    const { readOnly, isManager } = this.props;

    const selectedItem = menuItems.find(item => item.code === targetFilter);
    if (!selectedItem) {
      return [];
    }
    const canEdit = !readOnly && isManager;

    const buttons: (JSX.Element | DashboardSectionButton)[] = [];

    buttons.push(
      <SimpleTooltip text={'Switch group'}>
        <Dropdown
          isOpen={this.state.dropdownOpen}
          className='d-inline-block mr-2'
          toggle={this.handleDropdownToggle}>
          <DropdownToggle color='transparent' outline caret>
            {selectedItem.name}
          </DropdownToggle>
          <DropdownMenu end>
            {menuItems.map(item => (
              <DropdownItem key={`di-${item.code}`} active={targetFilter === item.code} onClick={() => this.handleTargetFilter(item.code)}>
                {item.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </SimpleTooltip>
    );

    if (canEdit && targetFilter === TargetFilter.Custom) {
      buttons.push({
        icon: 'fa-file',
        tooltip: 'Create a Custom Report using these metrics',
        className: 'mr-2',
        onClick: () => this.handleCreateCustomReport()
      });
    }

    if (canEdit) {
      buttons.push(<EditButton tooltip={'Add a Universal Tracker or Edit Material SDGs'} onClick={() => this.props.toggleTargetActualModal()} />);
    }

    return buttons;
  }

  handleDropdownToggle = () => {
    this.setState({ dropdownOpen: !this.state.dropdownOpen });
  }

  renderRemoveUTrModal() {
    const { showRemoveUtrModal, showRemoveUtrModalId, saving, apiMessage } = this.state;
    return <Modal isOpen={showRemoveUtrModal} toggle={this.handleCancel} backdrop='static'>
      <ModalHeader toggle={this.handleCancel}>Remove Universal Tracker</ModalHeader>
      <ModalBody>
        {saving && <Loader />}
        {apiMessage && <div className='alert alert-primary'>{apiMessage}</div>}
        Are you sure you would like to remove this Universal Tracker?
      </ModalBody>
      <ModalFooter>
        <Button color='link-secondary' className='mr-3' onClick={() => this.handleCancel()}>Cancel</Button>
        <Button disabled={apiMessage !== ''} onClick={() => this.handleRemoveUTr(showRemoveUtrModalId)}>Remove</Button>
      </ModalFooter>
    </Modal>
  }

  handleReload: () => Promise<void> = () => {
    return this.props.reloadInitiative();
  }


  renderTargetActualModal() {
    const { isManager, initiative, initiativeId } = this.props;
    if (!isManager) {
      return <></>;
    }

    const { apiMessage, saving } = this.state;

    const handleReloadScorecard = () => {
      this.props.reloadInitiative()
      this.props.reloadScoreCard(initiativeId, this.props.surveyId)
    }

    return (
      <TargetActualModal
        initiative={initiative}
        isOpen={this.props.openTargetActualModal}
        toggle={this.props.toggleTargetActualModal}
        handleReload={this.handleReload}
        handleReloadScoreCard={handleReloadScorecard}
        message={apiMessage}
        saving={saving}
        handleCancel={this.handleCancel}
        handleUpdate={this.handleAddUTr}
        handleClearMessage={this.handleClearMessage}
        activeTab={this.props.activeTab}
        setActiveTab={this.props.setActiveTab}
      />
    );
  }
}

const mapStateToProps = () => {
  return (state: RootState) => {
    return {
      bubbles: getBubbles(state),
      universalTrackerValuesState: state.universalTrackerValues,
      initiative: state.initiative.data,
      initiativeId: state.initiative.data?._id,
      materiality: state.initiative.data?.materiality,
      isManager: isUserManager(state),
    }
  }
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    loadUTrModal: (universalTrackerId: string) => dispatch(loadUniversalTrackerModal({ universalTrackerId: universalTrackerId })),
    reloadInitiative: () => dispatch(reloadInitiative()).then(() => dispatch(reloadUniversalTrackerValues())),
    reloadScoreCard: (initiativeId?: string, surveyId?: string) => {
      if(!initiativeId) {
        return
      }
      if(surveyId){
        dispatch(loadScorecardsBySurveyId(initiativeId, surveyId, false, undefined, true))
        return
      }
      dispatch(loadScorecardsByInitiativeId(initiativeId, false, undefined, true))
    }
  }
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>

export default withRouter(connector(TargetActual));
